import type { EffectiveOccurrence, MatrixPos, Priority, RecurrenceOverride, Task } from "@app/core";
import { getServices } from "../../platform.js";
import type { SeriesGet, SeriesSet } from "./types.js";

/**
 * Return a new overrides map with `o` inserted under its recurrence_id,
 * preserving ascending `recurrence_id_ref` order.
 *
 * For single-range overrides any previous single targeting the same
 * recurrence_id_ref is evicted first, so there is always at most one
 * single-override slot per occurrence.
 */
export function appendOverride(
  map: Record<string, RecurrenceOverride[]>,
  o: RecurrenceOverride,
): Record<string, RecurrenceOverride[]> {
  let base = map[o.recurrence_id] ?? [];
  if (o.range === "single") {
    base = base.filter(
      (e) => !(e.range === "single" && e.recurrence_id_ref === o.recurrence_id_ref),
    );
  }
  const list = [...base, o].sort((a, b) =>
    a.recurrence_id_ref.localeCompare(b.recurrence_id_ref),
  );
  return { ...map, [o.recurrence_id]: list };
}

/**
 * Insert or update a single-range override. When a single override already
 * exists for the same (recurrence_id, recurrence_id_ref) the existing DB row
 * is updated in-place (keeping its id / created_at) instead of creating a
 * duplicate. The in-memory store is kept consistent via appendOverride.
 */
export async function upsertSingleOverride(
  set: SeriesSet,
  get: SeriesGet,
  incoming: RecurrenceOverride,
): Promise<void> {
  const { recurrenceOverrides } = getServices();
  const existing = (get().overrides[incoming.recurrence_id] ?? []).find(
    (o) => o.range === "single" && o.recurrence_id_ref === incoming.recurrence_id_ref,
  );
  if (existing) {
    const updated = updateSingleOverride(existing, incoming);
    await recurrenceOverrides.update(updated);
    set({ overrides: appendOverride(get().overrides, updated) });
  } else {
    await recurrenceOverrides.insert(incoming);
    set({ overrides: appendOverride(get().overrides, incoming) });
  }
}

/** Merge a status operation into an existing occurrence row while preserving
 * its field edits. Local callers create status-only overrides, whose null
 * fields mean "not supplied" here; replacing them wholesale erased a moved or
 * renamed occurrence when it was completed/snoozed. `snoozed_until` is the
 * exception: status changes must be able to clear the previous wake time. */
export function updateSingleOverride(
  existing: RecurrenceOverride,
  incoming: RecurrenceOverride,
): RecurrenceOverride {
  return {
    ...existing,
    status: incoming.status,
    snoozed_until: incoming.snoozed_until,
    due_at: incoming.due_at ?? existing.due_at,
    duration_minutes: incoming.duration_minutes ?? existing.duration_minutes,
    title: incoming.title ?? existing.title,
    notes: incoming.notes ?? existing.notes,
    priority: incoming.priority ?? existing.priority,
    custom_color: incoming.custom_color ?? existing.custom_color,
    all_day: incoming.all_day ?? existing.all_day,
    rrule: incoming.rrule ?? existing.rrule,
    updated_at: incoming.updated_at,
  };
}

/**
 * Hard-delete every override matching `predicate` from the repo and return the
 * partitioned lists. Both `split-series` and `cap-series` previously open-coded
 * this filter / await-delete / recompute-remaining sequence.
 */
export async function partitionAndDelete(
  overrides: ReadonlyArray<RecurrenceOverride>,
  predicate: (o: RecurrenceOverride) => boolean,
): Promise<{ removed: RecurrenceOverride[]; remaining: RecurrenceOverride[] }> {
  const { recurrenceOverrides } = getServices();
  const removed: RecurrenceOverride[] = [];
  const remaining: RecurrenceOverride[] = [];
  for (const o of overrides) (predicate(o) ? removed : remaining).push(o);
  for (const o of removed) await recurrenceOverrides.hardDelete(o.id);
  return { removed, remaining };
}

/** Patch shape covering the 6 fields detach + split share. */
export type TaskFieldPatch = {
  title?: string | undefined;
  notes?: string | undefined;
  all_day?: boolean | undefined;
  duration_minutes?: number | undefined;
  priority?: Priority | undefined;
  custom_color?: string | null | undefined;
  is_event?: boolean | undefined;
  /** Topics for the new task. undefined = inherit the source's topics. */
  topic_ids?: string[] | undefined;
  /** Labels for the new task. undefined = inherit the source's labels. */
  label_ids?: string[] | undefined;
  /** Real nullable field — overrides the source tz on the new task. undefined = inherit. */
  tz?: string | null | undefined;
};

/**
 * Three-level fallback used when building a Task for detach/split:
 * patch field → effective value at split point → source task field.
 */
export function buildEffectiveTaskFields(
  patch: TaskFieldPatch,
  eff: EffectiveOccurrence | null,
  source: Task,
): {
  title: string;
  notes: string;
  all_day: boolean;
  duration_minutes: number;
  priority: Priority;
  matrix_pos: MatrixPos | null;
  matrix_hidden: boolean;
  custom_color: string | null;
  is_event: boolean;
  location: string;
  label_ids: string[];
  topic_ids: string[];
  calendar_id: string | null;
  reminder_offsets: number[];
  tz: string | null;
} {
  return {
    title: patch.title ?? (eff?.title ?? source.title),
    notes: patch.notes ?? (eff?.notes ?? source.notes),
    all_day: patch.all_day ?? (eff?.all_day ?? source.all_day),
    duration_minutes: patch.duration_minutes ?? (eff?.duration_minutes ?? source.duration_minutes),
    priority: patch.priority ?? (eff?.priority ?? source.priority),
    // custom_color is a tri-state: an explicit null CLEARS the colour, undefined
    // inherits. `??` would collapse null into the inherit path (silently restoring
    // the source colour when the user cleared it), so test for undefined like `tz`.
    custom_color: patch.custom_color !== undefined ? patch.custom_color : (eff?.custom_color ?? source.custom_color),
    is_event: patch.is_event ?? source.is_event,
    matrix_pos: source.matrix_pos,
    matrix_hidden: source.matrix_hidden,
    location: source.location,
    label_ids: patch.label_ids ?? source.label_ids,
    topic_ids: patch.topic_ids ?? source.topic_ids,
    calendar_id: source.calendar_id,
    // Whole-series fields (overrides never carry them) inherit from the source so
    // a split tail / detached occurrence keeps the series' reminders/calendar/
    // location. `tz`, `topic_ids` and `label_ids` are the exceptions: an explicit
    // patch lets a "this and following" / "this only" change land on the NEW
    // segment rather than the original (topics + labels follow the single/
    // following/all choice, like title/notes).
    reminder_offsets: source.reminder_offsets,
    tz: patch.tz !== undefined ? patch.tz : source.tz,
  };
}
